import { useEffect, useRef } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, useEditMode, EditableToken } from '@vibesites/runtime/react';

// Shared with the `flex-payment` injection block so the SDK is only ever loaded once.
const SCRIPT_ID = 'flex-payment-script';
const ANCHOR_ID = 'flex-widget';

type FlexWidgetVariant = 'horizontal' | 'square';
type FlexSquarePosition = 'left' | 'right' | 'unset';

interface FlexWidgetInstance {
  render: (elementId: string) => void;
}

interface FlexWidgetsGlobal {
  Horizontal: (options?: Record<string, unknown>) => FlexWidgetInstance;
  Square: (options?: { position?: FlexSquarePosition; dismissible?: boolean }) => FlexWidgetInstance;
}

declare global {
  interface Window {
    FlexWidgets?: FlexWidgetsGlobal;
  }
}

interface FlexPaymentWidgetProps {
  variant?: FlexWidgetVariant;
  position?: FlexSquarePosition;
  dismissible?: boolean;
}

export function FlexPaymentWidget({
  variant = 'horizontal',
  position = 'left',
  dismissible = false,
}: FlexPaymentWidgetProps) {
  const clientId = useContentToken('flexpaymentwidget.client_id');
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    const renderWidget = () => {
      const flex = window.FlexWidgets;
      const anchor = anchorRef.current;
      if (cancelled || !flex || !anchor) return;
      anchor.innerHTML = '';
      const widget =
        variant === 'square' ? flex.Square({ position, dismissible }) : flex.Horizontal();
      widget.render(ANCHOR_ID);
    };

    if (window.FlexWidgets) {
      renderWidget();
      return () => {
        cancelled = true;
      };
    }

    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `https://sdk.getflex.com/widgets/v1/widgets.js?clientId=${encodeURIComponent(clientId)}`;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', renderWidget);

    return () => {
      cancelled = true;
      script?.removeEventListener('load', renderWidget);
    };
  }, [clientId, variant, position, dismissible]);

  return (
    <section className="py-6 md:py-8 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {isEditMode && (
          <p className="mb-3 font-body text-xs text-[var(--vibe-color-text-muted)]">
            Flex Client ID:{' '}
            <EditableToken path="flexpaymentwidget.client_id">{clientId}</EditableToken>
          </p>
        )}
        <div id={ANCHOR_ID} ref={anchorRef} />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FlexPaymentWidget',
  displayName: 'Flex Payment Widget',
  tokens: {
    'flexpaymentwidget.client_id': {
      label: 'Flex Client ID',
      type: 'text',
      defaultValue: 'recOOKNu0x8bW8Ms9',
    },
  },
};

(FlexPaymentWidget as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FlexPaymentWidget;
